import { useEffect, useRef, useState } from "react";
import { resolveAddressThunk } from "app/features";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectReportsState } from "app/data/selectors";
import { hasResolvableCoordinates } from "app/data/helpers/reportCommonHelpers";

const coordKey = (latitude: number, longitude: number) =>
  `${latitude},${longitude}`;

const useResolveAddress = (data: any) => {
  const dispatch = useAppDispatch();
  const { resolvedAddresses } = useAppSelector(selectReportsState);

  const processedDataKey = useRef<string>("");
  const pendingKeysRef = useRef<Set<string>>(new Set());
  const [pendingRequests, setPendingRequests] = useState<Set<string>>(
    new Set()
  );
  const [resolvedAddressLoading, setResolvedAddressLoading] = useState(false);

  useEffect(() => {
    const currentDataKey = data?.length
      ? `${data.length}-${data.map((item: any) => item.uniqueId || item.id).join(",")}`
      : "";

    if (!data?.length) {
      processedDataKey.current = "";
      pendingKeysRef.current = new Set();
      setPendingRequests(new Set());
      setResolvedAddressLoading(false);
      return;
    }

    // Only start a new resolve batch when the dataset identity changes.
    if (currentDataKey === processedDataKey.current) {
      return;
    }

    processedDataKey.current = currentDataKey;

    const uniqueCoordinates = new Set<string>();
    const coordsToResolve: Array<{
      key: string;
      latitude: number;
      longitude: number;
    }> = [];

    data.forEach((row: any) => {
      const pairs = [
        {
          latitude: row.startLatitude,
          longitude: row.startLongitude
        },
        {
          latitude: row.endLatitude,
          longitude: row.endLongitude
        }
      ];

      pairs.forEach(({ latitude, longitude }) => {
        if (!hasResolvableCoordinates(latitude, longitude)) {
          return;
        }

        const key = coordKey(latitude, longitude);
        if (uniqueCoordinates.has(key)) {
          return;
        }
        uniqueCoordinates.add(key);

        const existingAddress = resolvedAddresses.find(
          (item) => item.latitude === latitude && item.longitude === longitude
        );

        if (!existingAddress) {
          coordsToResolve.push({ key, latitude, longitude });
        }
      });
    });

    if (coordsToResolve.length === 0) {
      pendingKeysRef.current = new Set();
      setPendingRequests(new Set());
      setResolvedAddressLoading(false);
      return;
    }

    const pendingKeys = new Set(coordsToResolve.map((item) => item.key));
    pendingKeysRef.current = pendingKeys;
    setPendingRequests(new Set(pendingKeys));
    setResolvedAddressLoading(true);

    coordsToResolve.forEach(({ key, latitude, longitude }) => {
      dispatch(
        resolveAddressThunk({
          latitude,
          longitude
        })
      ).finally(() => {
        if (!pendingKeysRef.current.has(key)) {
          return;
        }

        pendingKeysRef.current.delete(key);
        const nextPending = new Set(pendingKeysRef.current);
        setPendingRequests(nextPending);

        if (nextPending.size === 0) {
          setResolvedAddressLoading(false);
        }
      });
    });
    // Depend on `data` only so address results re-render via the selector
    // without restarting the resolve batch.
  }, [data, dispatch]);

  return {
    resolvedAddresses,
    resolvedAddressLoading,
    pendingRequests
  };
};

export default useResolveAddress;
